import React, { useState, useEffect } from 'react';
import LayoutAndQuoteBuilderNavSection from 'components/layout_and_quote_builder/LayoutAndQuoteBuilderNavSection.jsx';
import downloadButtonsPresent from 'components/common/downloadButtonsPresent';
import {
  writeSelectedSolutionUuids,
  clearSelectedSolution
} from 'components/layout_and_quote_builder/utils/solutionUrlState.js';

// The diecutting_result.uuid of each solution in a section's selected combination. Anything that is not
// an array — including the `() => []` sentinel the reset path passes as the selectedList — means "no
// solution selected" and yields [] (which clears the URL); the function is never invoked here.
const solutionUuidsOf = (selectedList) => {
  if (!Array.isArray(selectedList)) { return []; }
  return selectedList.map((member) => member?.diecutting_result?.uuid).filter(Boolean);
};

// Mirror a section's selected solution into the address bar (see solutionUrlState): its uuid(s) go in
// ?solution_<index>=..., so the link is shareable and self-describing (the parameter's presence means a
// solution is selected). Cleared when nothing is selected. The saved quote (DB) stays the source of truth.
const reflectSolutionSelectionInUrl = (index, selectedList) => {
  const uuids = solutionUuidsOf(selectedList);
  if (uuids.length > 0) {
    writeSelectedSolutionUuids(index, uuids);
  } else {
    clearSelectedSolution(index);
  }
};

const LayoutNavSectionList = (props) => {
  const [requiredParts, setRequiredPartsInternal] = useState([]);
  const [selectedParts, setSelectedPartsInternal] = useState({});
  const [selectedPartsNormalised, setSelectedPartsNormalised] = useState([]);

  const [selectedMembers, setSelectedMembersInternal] = useState({});
  const [selectedMembersNormalised, setSelectedMembersNormalised] = useState([]);

  const [activeRedraws, setActiveRedraws] = useState([]);

  // Version counter to force re-render when nav sections change
  const [navSectionsVersion, setNavSectionsVersion] = useState(0);

  // Track selectedList for each section by index
  // Initialize from saved user_quote_nav_sections if available
  const getInitialSelectedLists = () => {
    const initialLists = {};
    if (window.user_quote_nav_sections) {
      window.user_quote_nav_sections.forEach((navSection) => {
        if (navSection.selected_lists && navSection.selected_lists.length > 0) {
          initialLists[navSection.sort_order] = navSection.selected_lists;
        }
      });
    }
    return initialLists;
  };
  const [sectionSelectedLists, setSectionSelectedLists] = useState(getInitialSelectedLists);

  const setRequiredParts = (parts) => {
    setRequiredPartsInternal( (previous) => previous.concat(parts) )
  }

  const setSelectedParts = (parts, id) => {
    setSelectedPartsInternal( (previous) => {
      previous[id] = parts;
      return { ...previous };
    });
  }

  const updateSelectedPartsNormalised = () => {
    let res = [];
    for (const index in selectedParts) {
      res = res.concat(selectedParts[index]).flat();
    }
    setSelectedPartsNormalised(res);
  }

  const setSelectedMembers = (members, id) => {
    setSelectedMembersInternal( (previous) => {
      previous[id] = members;
      return { ...previous };
    });

    setSelectedParts(members.map((member) => member.part_names).flat(), id);
  }

  const updateSelectedMembersNormalised = () => {
    let res = [];
    for (const index in selectedMembers) {
      res = res.concat(selectedMembers[index]).flat();
    }
    setSelectedMembersNormalised(res);
  }

  const missingParts = () => {
    return requiredParts.filter((part) => { return selectedPartsNormalised.indexOf(part) == -1});
  }

  const setSectionSelectedList = (index, selectedList) => {
    reflectSolutionSelectionInUrl(index, selectedList);
    setSectionSelectedLists((previous) => {
      previous[index] = selectedList;
      return { ...previous };
    });
  }

  // Listen for navSectionsUpdated event to update linked sections
  useEffect(() => {
    const handleNavSectionsUpdated = (event) => {
      const { windowNavSections, sourceIndex } = event.detail;
      if (!windowNavSections) {
        trackEvent('js_error_happened', {
          exception: '[LayoutNavSectionList#handleNavSectionsUpdated] windowNavSections is undefined',
          sourceIndex: sourceIndex,
          user_quote_id: window.user_quote_id
        });
        return;
      }

      // Find sections linked to the source section
      const sourceSection = windowNavSections.find(section => section.sort_order === sourceIndex);
      if (!sourceSection) {
        trackEvent('js_error_happened', {
          exception: '[LayoutNavSectionList#handleNavSectionsUpdated] sourceSection not found',
          sourceIndex: sourceIndex,
          windowNavSections_length: windowNavSections.length,
          user_quote_id: window.user_quote_id
        });
        return;
      }

      // Update sectionSelectedLists for sections linked to the source
      windowNavSections.forEach((navSection) => {
        // Skip if this section doesn't have a solution link
        if (!navSection.solution_user_quote_nav_section_id) {
          return;
        }

        // Check if this section is linked to the source section
        if (navSection.solution_user_quote_nav_section_id !== sourceSection.id) {
          return;
        }

        // Only update if the section has selected_lists from its parent
        if (navSection.selected_lists && navSection.selected_lists.length > 0) {
          // A linked section inherited a solution — mirror it into the URL too.
          reflectSolutionSelectionInUrl(navSection.sort_order, navSection.selected_lists);
          setSectionSelectedLists((previous) => {
            const currentList = previous[navSection.sort_order];
            const newList = navSection.selected_lists;

            // Deep compare to avoid unnecessary updates
            if (JSON.stringify(currentList) === JSON.stringify(newList)) {
              return previous;
            }

            return { ...previous, [navSection.sort_order]: newList };
          });
        }
      });
    };

    window.addEventListener('navSectionsUpdated', handleNavSectionsUpdated);
    return () => {
      window.removeEventListener('navSectionsUpdated', handleNavSectionsUpdated);
    };
  }, []);

  // Listen for navSectionsReset event to reset linked sections
  useEffect(() => {
    const handleNavSectionsReset = (event) => {
      const { resetIndices, sourceIndex, windowNavSections } = event.detail;

      // Update window.user_quote_nav_sections if provided
      if (windowNavSections) {
        window.user_quote_nav_sections = windowNavSections;
      }

      // Reset sectionSelectedLists for the source section and linked sections
      setSectionSelectedLists((previous) => {
        const updated = { ...previous };

        // Reset the source section
        if (sourceIndex !== undefined) {
          updated[sourceIndex] = [];
          clearSelectedSolution(sourceIndex);
        }

        // Reset linked sections
        if (resetIndices && resetIndices.length > 0) {
          resetIndices.forEach((index) => {
            updated[index] = [];
            clearSelectedSolution(index);
          });
        }

        return updated;
      });

      // Increment version to force re-render so shouldHideSection is re-evaluated
      setNavSectionsVersion((prev) => prev + 1);
    };

    window.addEventListener('navSectionsReset', handleNavSectionsReset);
    return () => {
      window.removeEventListener('navSectionsReset', handleNavSectionsReset);
    };
  }, []);

  // On load the saved quote already restores selected solutions from the DB (getInitialSelectedLists);
  // reflect them into the address bar too, so a freshly-opened / bare link becomes self-describing and
  // shareable. replaceState via the helper, so this adds no history entries. This keys the parameter by
  // the section's sort_order, which the rest of this component treats as identical to the render array
  // index (sectionSelectedLists is stored by sort_order and read by index) — the same key the live
  // selection and the manufacturing_line_<index> parameter use, so all three stay aligned.
  useEffect(() => {
    Object.entries(getInitialSelectedLists()).forEach(([sortOrder, selectedList]) => {
      const uuids = solutionUuidsOf(selectedList);
      if (uuids.length > 0) {
        writeSelectedSolutionUuids(Number(sortOrder), uuids);
      }
    });
  }, []);

  useEffect(updateSelectedPartsNormalised, [selectedParts]);
  useEffect(updateSelectedMembersNormalised, [selectedMembers]);
  useEffect(() =>{ window.selectedMembersNormalised = selectedMembersNormalised} , [selectedMembersNormalised]);

  useEffect(() => {
    if(activeRedraws.length > 0) {
      $(".RedrawLayoutButton").attr('disabled', 'disabled');
      $(".RedrawLayoutButtonImg").attr('src', window.redrawLoadingPngUrl);
      if (downloadButtonsPresent()) { disableDownloadButtons(); }
    } else {
      if (downloadButtonsPresent()) { enableDownloadButtons(); }
    }

    $("#RedrawLayoutButton").removeAttr('disabled');
    $("#RedrawLayoutButtonImg").attr('src', window.redrawRefreshPngUrl);
  }, [activeRedraws]);

  // Linked sections (e.g. corrugated_cardboard_offset) are no longer fully hidden until their parent
  // is solved. They render with their own "Start Working" enable button (see
  // LayoutAndQuoteBuilderNavSection), gated until the parent's solution is inherited.
  const shouldHideSection = () => false;

  return (<>
    {props.navSections.map((navSection, index) => {
      // Skip rendering sections that get their solution from another section
      if (shouldHideSection(navSection)) {
        return null;
      }

      const currentSelectedList = sectionSelectedLists[index] || [];

      return <LayoutAndQuoteBuilderNavSection
        key={`navSection_${index}_v${navSectionsVersion}`}
        setSelectedMembers={setSelectedMembers}
        activeRedraws={activeRedraws}
        setActiveRedraws = {setActiveRedraws}
        navSection={navSection}
        totalNavSectionSize={props.navSections.length}
        index={index}
        setRequiredParts={setRequiredParts}
        count={props.count}
        config={props.config}
        selectedList={currentSelectedList}
        setSelectedList={(selectedList) => setSectionSelectedList(index, selectedList)}
        stageView={props.stageView}
        inWizard={props.inWizard}
        onWizardPickedChange={props.onWizardPickedChange}
        onWizardSelectionChange={props.onWizardSelectionChange}
        onWizardMachinePriceChange={props.onWizardMachinePriceChange}
        wizardSetupSubmitRef={props.wizardSetupSubmitRef}
        onWizardSetupResolved={props.onWizardSetupResolved}
        onWizardSetupSaveError={props.onWizardSetupSaveError}
        onLinePicked={props.onLinePicked}
        onSolutionPicked={props.onSolutionPicked}
        onGoToNextStage={props.onGoToNextStage}
        onGoToManufacturingLine={props.onGoToManufacturingLine}
      />})}
    </>
  )
}

export default LayoutNavSectionList;
